/**
 * Spectrum BXAY bundle cart engine.
 *
 * Adapter over the storefront bundle-cart SDK (`window.__spectrumAi.bundle`,
 * with a `window.Spectrum.bundle` fallback for older builds). The
 * server owns the cart and the discount: every mutation returns the enriched
 * bundle cart (items + `spectrumBundleProgress` + `spectrumBundleCost` +
 * `checkoutUrl`), with the fixed-bundle-price discount code already applied by
 * the backend's auto-discount pipeline. This engine holds no pricing logic and
 * mints no discount codes — it maps the backend response into the state shape
 * the runtime's bottom bar paints, and forwards checkout to the server's
 * `checkoutUrl`. Because the discount is applied server-side to the real cart,
 * the on-page total (`spectrumBundleCost.totalAmount`) is the amount charged at
 * checkout — there is no client-computed price that can drift from it.
 *
 * Requires the bundle to be bound (`config.bundleSlug`) and its bundle cart to
 * be enabled in the portal; without either, the backend has nothing to serve
 * and the engine stays inert (and says so) rather than faking a cart.
 *
 * Public surface (consumed by the runtime in sai-snippet-z99nr29s.js):
 *   init(config)            – bind to the bundle slug + hydrate from the server
 *   add(line)               – async; append one unit of a variant, returns ok
 *   remove(index)           – async; drop the unit at a bottom-bar index
 *   clear()                 – async; empty the bundle cart
 *   getState()              – sync snapshot { lines, count, tier, total, savings, canCheckout }
 *   countForProduct(handle) – units of a product currently in the cart
 *   canAdd(line)            – sync display gate for the Mini-PDP CTA
 *   subscribe(cb)           – returns an unsubscribe fn; cb fires on every change
 *   checkout()              – async; redirect to the server checkoutUrl
 */
;(() => {
  const win = globalThis.window
  const NS = win.Spectrum ?? {}
  if (NS.z99nr29sCart) return // already mounted
  win.Spectrum = NS

  /** @type {{
   *   bundleSlug: string | null,
   *   config: any | null,
   *   lastData: any | null,
   *   subscribers: Set<Function>,
   * }} */
  const state = {
    bundleSlug: null,
    config: null,
    lastData: null,
    subscribers: new Set(),
  }

  function bundleApi() {
    // The bundle-cart SDK is published by the edge cart SDK on
    // `window.__spectrumAi.bundle` (the same location SpectrumSDK's own
    // getCheckoutUrl reads). Older builds / the unit tests expose it on
    // `window.Spectrum.bundle`. Prefer the edge SDK, fall back to Spectrum.
    return win.__spectrumAi?.bundle ?? win.Spectrum?.bundle ?? null
  }

  /** Map a backend BundleCartResponse into the runtime's bottom-bar state.
   *  Pure — no side effects, no SDK calls — so it can be unit-tested directly.
   *
   *  The bundle cart collapses repeated picks of one variant into a single line
   *  with quantity N; thumbnails want one entry per unit, so each line expands
   *  to `quantity` entries. Pricing comes from `spectrumBundleCost` (the
   *  server's authoritative, discount-applied totals), never from line math. */
  function deriveState(data, config) {
    const progress = data?.spectrumBundleProgress ?? null
    const cost = data?.spectrumBundleCost ?? null
    const items = Array.isArray(data?.items) ? data.items : []

    const lines = items.flatMap((it) => {
      const entry = {
        productHandle: it.handle,
        variantId: it.variant_id,
        image: it.image,
        displayLabel: it.product_title,
        price: it.final_price,
        compareAtPrice: it.original_price,
      }
      const qty = Math.max(1, Number(it.quantity) || 1)
      return Array.from({ length: qty }, () => entry)
    })

    // One entry per cart item (per variant) with its quantity — drives the
    // per-product stepper and the expanded-bar rows. `lines` (expanded per
    // unit) stays for the thumbnail strip.
    const groups = items.map((it) => ({
      variantId: it.variant_id,
      productHandle: it.handle,
      displayLabel: it.product_title,
      variantTitle: it.variant_title ?? '',
      options: Array.isArray(it.options_with_values) ? it.options_with_values : [],
      image: it.image,
      price: it.final_price,
      compareAtPrice: it.original_price,
      quantity: Math.max(1, Number(it.quantity) || 1),
    }))

    const count = progress?.eligibleItemCount ?? lines.length
    const required = progress?.requiredItemCount ?? config?.quantityRequirement ?? 0
    const isMet = !!progress?.isSatisfied
    const total = cost?.totalAmount ?? 0
    const retailTotal = cost?.totalMRP ?? 0
    const savings = cost?.applicableDiscount ?? 0
    const partialOk = !!config?.partialCheckoutAllowed

    return {
      lines,
      groups,
      count,
      // Single-tier shape: the backend `buy_x_at_y` progress has no tier ladder
      // (multi-tier maps to volume_discount, out of scope here). `nextTier` is
      // null so the runtime's multi-tier UI stays dormant.
      tier: { quantity: required, price: total, label: null, isMet, isExact: isMet, nextTier: null },
      total,
      retailTotal,
      savings,
      canCheckout: isMet || (partialOk && count > 0),
    }
  }

  function getState() {
    return deriveState(state.lastData, state.config)
  }

  function notify() {
    const snapshot = getState()
    for (const cb of state.subscribers) {
      try {
        cb(snapshot)
      } catch (err) {
        console.warn('[z99nr29s-cart] subscriber failed', err)
      }
    }
  }

  /** Await an SDK call, store its cart on success, and notify. Failures surface
   *  loudly — a rejected add (out of stock, cap, not-enabled) must not look
   *  like a silent success. */
  async function commit(action, promise) {
    let result
    try {
      result = await promise
    } catch (err) {
      console.warn(`[z99nr29s-cart] ${action} threw`, err)
      return { ok: false, error: String(err) }
    }
    if (result?.ok && result.data) {
      state.lastData = result.data
      notify()
    } else {
      console.warn(`[z99nr29s-cart] ${action} failed`, result?.error ?? result)
    }
    return result
  }

  const api = {
    async init(config) {
      state.config = config || {}
      state.bundleSlug = config?.bundleSlug ?? null
      const bundle = bundleApi()
      if (!bundle || !state.bundleSlug) {
        // Fail loud: no storefront bundle SDK or no bound bundle = nothing to
        // talk to. Notify once with the empty snapshot so the UI renders its
        // empty state instead of hanging.
        console.warn('[z99nr29s-cart] inactive — missing bundle SDK or config.bundleSlug', {
          hasSdk: !!bundle,
          bundleSlug: state.bundleSlug,
        })
        notify()
        return
      }
      await commit('init', bundle.getCart(state.bundleSlug))
    },

    async add(line, qty = 1) {
      const bundle = bundleApi()
      if (!bundle || !state.bundleSlug) return false
      const quantity = Math.max(1, Math.floor(Number(qty) || 1))
      const result = await commit('add', bundle.addCart(state.bundleSlug, [
        { id: Number(line.variantId), quantity },
      ]))
      return !!result?.ok
    },

    async remove(index) {
      const bundle = bundleApi()
      if (!bundle || !state.bundleSlug) return false
      const lines = getState().lines
      const line = lines[index]
      if (!line) return false
      // Collapse to per-variant quantity: the units remaining for this variant
      // after dropping the one at `index`.
      const variantId = line.variantId
      const remaining = lines.filter((l, i) => l.variantId === variantId && i !== index).length
      const result = await commit(
        'remove',
        bundle.changeCart(state.bundleSlug, { id: Number(variantId), quantity: remaining }),
      )
      return !!result?.ok
    },

    async clear() {
      const bundle = bundleApi()
      if (!bundle || !state.bundleSlug) return
      await commit('clear', bundle.clearCart(state.bundleSlug))
    },

    /** Fixed curated-bundle add: the WHOLE manifest in one cart action.
     *  Bound (bundleSlug) → one server bundle-cart `addCart` call; the backend
     *  owns the discounted total, and we return its authoritative savings so
     *  the caller can reconcile the displayed figure. Unbound → one native
     *  `/cart/add.js { items: [] }` multi-line add (Shopify batches every line
     *  in a single request); savings then come from the variants' own
     *  compare-at, which the plain cart honours. Fail loud: a rejected add
     *  returns `{ ok: false }`, never a fake success. */
    async addManifest(items, bundleSlug) {
      const slug = bundleSlug || state.bundleSlug
      const lines = (Array.isArray(items) ? items : []).map((i) => ({
        id: Number(i.id),
        quantity: Math.max(1, Math.floor(Number(i.quantity) || 1)),
      }))
      if (lines.length === 0) return { ok: false }

      const bundle = bundleApi()
      if (bundle && slug) {
        const result = await commit('addManifest', bundle.addCart(slug, lines))
        if (!result?.ok) return { ok: false }
        const cost = state.lastData?.spectrumBundleCost ?? null
        const serverSavings =
          cost?.applicableDiscount ??
          (cost ? (cost.totalMRP ?? 0) - (cost.totalAmount ?? 0) : null)
        return { ok: true, checkoutUrl: state.lastData?.checkoutUrl ?? '/checkout', serverSavings }
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lines }),
        })
        if (!res.ok) {
          console.warn('[z99nr29s-cart] addManifest native add rejected', res.status)
          return { ok: false }
        }
        return { ok: true, checkoutUrl: '/checkout', serverSavings: null }
      } catch (err) {
        console.warn('[z99nr29s-cart] addManifest native add threw', err)
        return { ok: false }
      }
    },

    getState,

    countForProduct(handle) {
      return getState().lines.filter((l) => l.productHandle === handle).length
    },

    /** Quantity of a specific variant currently in the bundle. */
    quantityForVariant(variantId) {
      return (
        getState().groups.find((g) => String(g.variantId) === String(variantId))?.quantity ?? 0
      )
    },

    /** The added variant group(s) for a product — drives the per-card stepper
     *  (one-variant-per-product is the common case; the array supports more). */
    groupsForProduct(handle) {
      return getState().groups.filter((g) => g.productHandle === handle)
    },

    /** Set the absolute quantity of a variant (0 removes it). Used by the
     *  per-product stepper and the expanded-bar quantity / trash controls. */
    async setQuantity(variantId, qty) {
      const bundle = bundleApi()
      if (!bundle || !state.bundleSlug) return false
      const next = Math.max(0, Math.floor(Number(qty) || 0))
      const result = await commit(
        'setQuantity',
        bundle.changeCart(state.bundleSlug, { id: Number(variantId), quantity: next }),
      )
      return !!result?.ok
    },

    /** Synchronous display gate for the Mini-PDP CTA. The backend is the
     *  authority on the actual add (cap, qualification); this only dims the
     *  button when the visible cart is already at the requirement, or the same
     *  product is in and the page restricts to one per product. */
    canAdd(line) {
      const s = getState()
      const cap = state.config?.quantityRequirement || Number.POSITIVE_INFINITY
      if (s.count >= cap) return false
      if (
        state.config?.quantityRestriction === 'max_one_per_product' &&
        line?.productHandle &&
        s.lines.some((l) => l.productHandle === line.productHandle)
      ) {
        return false
      }
      return true
    },

    subscribe(cb) {
      state.subscribers.add(cb)
      try {
        cb(getState())
      } catch {}
      return () => state.subscribers.delete(cb)
    },

    /** Redirect to the server-provided checkout URL. The bundle cart already
     *  holds the items with the fixed-price discount applied, so checkout is a
     *  plain navigation — no client discount handling. Re-fetch first so the
     *  URL reflects the final cart state. */
    async checkout() {
      const bundle = bundleApi()
      if (!bundle || !state.bundleSlug) return { ok: false, reason: 'no_bundle' }
      await commit('checkout', bundle.getCart(state.bundleSlug))
      const url = state.lastData?.checkoutUrl
      if (!url) return { ok: false, reason: 'no_checkout_url' }
      win.location.href = url
      return { ok: true }
    },
  }

  // Test hook — exposes the pure response→state mapping so it can be exercised
  // without a window or the bundle SDK. Gated on a sentinel production never sets.
  if (typeof __SAI_BXAY_TEST__ !== 'undefined') {
    /* istanbul ignore next */
    api.__test = { deriveState }
  }

  NS.z99nr29sCart = api
})()
